// Pure helpers for the incident Why-Why Analysis tree.
//
// The tree is stored (and sent to POST /api/incidents/{id}/rca/why-why) as a
// flat node list:
//   { id, parent_id, text, is_root_cause }
// parent_id null => a direct "why" of the problem statement. These helpers
// never mutate their input. The backend re-validates everything
// (services/why_why_tree.py), so these only shape the editor state.

export const MAX_WHY_WHY_NODES = 200;

let idCounter = 0;

/** A short id unique within this session (the backend caps ids at 64 chars). */
export function newNodeId() {
  idCounter += 1;
  return `n${Date.now().toString(36)}${idCounter.toString(36)}`;
}

/**
 * Nest the flat list into [{...node, children: [...]}] roots, keeping input
 * order among siblings. A node whose parent is missing (or that sits in a
 * cycle) is promoted to a root so nothing is silently hidden in the editor.
 */
export function buildTree(nodes) {
  const list = Array.isArray(nodes) ? nodes : [];
  const byId = new Map();
  list.forEach((n) => byId.set(n.id, { ...n, children: [] }));

  const roots = [];
  list.forEach((n) => {
    const item = byId.get(n.id);
    if (item.attached) return; // duplicate id (backend rejects these): render once
    item.attached = true;
    const parentId = n.parent_id;
    if (parentId != null && parentId !== '' && byId.has(parentId) && !isAncestor(list, n.id, parentId)) {
      byId.get(parentId).children.push(item);
    } else {
      roots.push(item);
    }
  });

  const strip = (item) => {
    const { attached, ...rest } = item;
    return { ...rest, children: rest.children.map(strip) };
  };
  return roots.map(strip);
}

// True when nodeId is an ancestor of (or equal to) candidateParentId, i.e.
// attaching nodeId under candidateParentId would close a cycle. Walks up the
// parent chain from candidateParentId looking for nodeId.
function isAncestor(nodes, nodeId, candidateParentId) {
  const parentOf = new Map(nodes.map((n) => [n.id, n.parent_id]));
  let current = candidateParentId;
  let steps = 0;
  while (current != null && current !== '' && steps <= nodes.length) {
    if (current === nodeId) return true;
    current = parentOf.get(current);
    steps += 1;
  }
  return false;
}

/** Ids of `id` and all of its descendants. */
export function subtreeIds(nodes, id) {
  const list = Array.isArray(nodes) ? nodes : [];
  const ids = new Set([id]);
  let grew = true;
  while (grew) {
    grew = false;
    list.forEach((n) => {
      if (!ids.has(n.id) && ids.has(n.parent_id)) {
        ids.add(n.id);
        grew = true;
      }
    });
  }
  return ids;
}

/** The list without node `id` and its whole subtree. */
export function removeSubtree(nodes, id) {
  const doomed = subtreeIds(nodes, id);
  return (Array.isArray(nodes) ? nodes : []).filter((n) => !doomed.has(n.id));
}

/** Append a new empty "why" under `parentId` (null = under the problem statement). */
export function addChild(nodes, parentId, id = newNodeId()) {
  return [
    ...(Array.isArray(nodes) ? nodes : []),
    { id, parent_id: parentId == null ? null : parentId, text: '', is_root_cause: false },
  ];
}

/** Replace fields on one node. */
export function updateNode(nodes, id, changes) {
  return (Array.isArray(nodes) ? nodes : []).map((n) => (n.id === id ? { ...n, ...changes } : n));
}

/** Flip the root-cause flag on one node. */
export function toggleRootCause(nodes, id) {
  return (Array.isArray(nodes) ? nodes : []).map((n) =>
    n.id === id ? { ...n, is_root_cause: !n.is_root_cause } : n
  );
}

/** Nodes normalised for saving: trimmed text, parent_id null rather than ''. */
export function toSavePayload(nodes) {
  return (Array.isArray(nodes) ? nodes : []).map((n) => ({
    id: n.id,
    parent_id: n.parent_id == null || n.parent_id === '' ? null : n.parent_id,
    text: (n.text || '').trim(),
    is_root_cause: Boolean(n.is_root_cause),
  }));
}
